import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/project/integrations/supabase/client";
import { institutions } from "@/project/data/sample";
import { campaigns, applications as seedApplicationsList } from "@/project/data/network";

export type OrgRow = {
  id: string;
  slug: string;
  name: string;
  org_type: string | null;
  domain: string | null;
  city: string | null;
  state: string | null;
  summary: string | null;
  description: string | null;
  cover_url: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  registration_no: string | null;
  status: string;
  verified: boolean;
  eligible_80g: boolean;
  animals_cared: number | null;
  capacity: number | null;
  total_raised: number | null;
  donor_count: number | null;
  created_at: string;
};

export type CampaignRow = {
  id: string;
  slug: string;
  org_id: string | null;
  title: string;
  domain: string | null;
  category: string | null;
  summary: string | null;
  description: string | null;
  image_url: string | null;
  goal: number;
  raised: number;
  urgency: string | null;
  ends_on: string | null;
  status: string;
  featured: boolean;
  created_at: string;
};

// Initial seed mock data for fallback offline/demo operations
const fallbackOrgs: OrgRow[] = institutions.map((i) => ({
  id: i.id,
  slug: i.id,
  name: i.name,
  org_type: i.type,
  domain: "animal-seva",
  city: i.city,
  state: i.state,
  summary: i.about.slice(0, 180),
  description: i.about,
  cover_url: i.cover,
  contact_email: i.email,
  contact_phone: i.phone,
  registration_no: i.registrationNo,
  status: i.verified ? "approved" : "pending",
  verified: i.verified,
  eligible_80g: i.has80G,
  animals_cared: i.animalCount,
  capacity: i.shelterCapacity,
  total_raised: i.totalDonations,
  donor_count: Math.round(i.totalDonations / 2100),
  created_at: new Date(Date.now() - 90 * 86400000).toISOString(),
}));

const fallbackCampaigns: CampaignRow[] = campaigns.map((c) => ({
  id: c.id,
  slug: c.id,
  org_id: c.orgId,
  title: c.title,
  domain: c.domain,
  category: "animal-seva",
  summary: c.summary,
  description: c.story,
  image_url: c.image,
  goal: c.goal,
  raised: c.raised,
  urgency: c.urgency,
  ends_on: c.endDate,
  status: "approved",
  featured: c.urgency === "Critical" || c.urgency === "Urgent",
  created_at: new Date(Date.now() - 30 * 86400000).toISOString(),
}));

// Global seed state for in-memory mutations in demo mode
let localDonations: any[] = [
  {
    id: "don_101",
    receipt_no: "DA-2026-89401",
    created_at: new Date(Date.now() - 2 * 3600000).toISOString(),
    donor_name: "Rajesh Kumar Patel",
    donor_email: "rajesh.patel@example.com",
    donor_phone: "+91 98250 11223",
    donor_pan: "ABCDE1234F",
    anonymous: false,
    amount: 5100,
    frequency: "once",
    donation_type: "monetary",
    org_id: "shree-ahmedabad-panjrapole",
    target_label: "Green Fodder Truckload for Naroda Panjrapole",
    status: "success",
    gateway: "Razorpay",
    gateway_ref: "pay_Nzs82910Jk",
  },
  {
    id: "don_102",
    receipt_no: "DA-2026-89402",
    created_at: new Date(Date.now() - 6 * 3600000).toISOString(),
    donor_name: "Mehta Family Trust",
    donor_email: "contact@mehtatrust.example",
    donor_phone: "+91 98980 44556",
    donor_pan: "MEHTA5678G",
    anonymous: false,
    amount: 15000,
    frequency: "once",
    donation_type: "inkind",
    item_name: "Green Fodder (Leelo Chhro / Chara)",
    quantity: "3 Tonnes (3000 kg)",
    dispatch_status: "Delivered to Gaushala",
    vendor_name: "Gujarat Farmers Krishi Cooperative",
    org_id: "shree-ahmedabad-panjrapole",
    target_label: "In-Kind Green Fodder Supply",
    status: "success",
    gateway: "UPI / GPay",
    gateway_ref: "pay_Nzs99100Ab",
  },
  {
    id: "don_103",
    receipt_no: "DA-2026-89403",
    created_at: new Date(Date.now() - 14 * 3600000).toISOString(),
    donor_name: "Dr. Ananya Sharma",
    donor_email: "ananya.sharma@example.com",
    donor_phone: "+91 97110 88990",
    donor_pan: "SHARM9012K",
    anonymous: false,
    amount: 2500,
    frequency: "monthly",
    donation_type: "monetary",
    org_id: "jivdaya-charitable-trust",
    target_label: "Avian ICU Emergency Surgery Fund",
    status: "success",
    gateway: "HDFC NetBanking",
    gateway_ref: "pay_Nzs77301Xy",
  },
  {
    id: "don_104",
    receipt_no: "DA-2026-89404",
    created_at: new Date(Date.now() - 28 * 3600000).toISOString(),
    donor_name: "Anonymous Sevak",
    donor_email: "",
    donor_phone: "",
    donor_pan: "",
    anonymous: true,
    amount: 1200,
    frequency: "once",
    donation_type: "inkind",
    item_name: "Emergency Medical & Wound Kit",
    quantity: "1 Medical Kit",
    dispatch_status: "In Transit",
    vendor_name: "Karuna Pharma Supplies",
    org_id: "people-for-animals-gandhinagar",
    target_label: "Stray Dog Vaccination Drive",
    status: "success",
    gateway: "PhonePe",
    gateway_ref: "pay_Nzs55209Mn",
  },
];

let localApplications: any[] = [
  ...seedApplicationsList.map((a) => ({
    id: a.id,
    org_name: a.org,
    org_type: a.type,
    domain: a.domain,
    city: a.city,
    state: "Gujarat",
    contact_name: "Trustee Office",
    contact_email: `contact@${a.id.toLowerCase()}.example`,
    contact_phone: "+91 98250 88112",
    registration_no: `TR/GUJ/2026/${a.id}`,
    reg_80g: `80G/AHM/2026/${a.id}`,
    reg_12a: `12A/AHM/2026/${a.id}`,
    animals_cared: 650,
    about: `${a.org} is an active animal welfare institution operating in ${a.city}.`,
    status: a.stage === "Approved" ? "approved" : "pending",
    created_at: new Date(a.submitted).toISOString(),
  })),
  {
    id: "app_101",
    org_name: "Gir Gau Suraksha Sansthan",
    org_type: "Gaushala",
    domain: "animal-seva",
    city: "Junagadh",
    state: "Gujarat",
    contact_name: "Valjibhai Rabari",
    contact_email: "valjibhai@girgau.example",
    contact_phone: "+91 94262 99001",
    registration_no: "GUJ/GAU/2021/88",
    reg_80g: "80G/AHM/2022/4412",
    reg_12a: "12A/AHM/2021/3309",
    animals_cared: 850,
    about: "Dedicated shelter protecting indigenous Gir cows and calves near Gir National Park.",
    status: "pending",
    created_at: new Date(Date.now() - 24 * 3600000).toISOString(),
  },
];

let localPayouts: any[] = [
  {
    id: "pay_01",
    org_id: "shree-ahmedabad-panjrapole",
    org_name: "Shree Ahmedabad Panjrapole",
    gross_amount: 1054200,
    platform_fee: 0,
    net_payout: 1054200,
    bank_account: "ICICI Bank · A/C ****4812",
    utr_no: "UTR20260801994821",
    status: "Transferred",
    created_at: new Date(Date.now() - 7 * 86400000).toISOString(),
  },
  {
    id: "pay_02",
    org_id: "jivdaya-charitable-trust",
    org_name: "Jivdaya Charitable Trust",
    gross_amount: 784200,
    platform_fee: 0,
    net_payout: 784200,
    bank_account: "HDFC Bank · A/C ****9014",
    utr_no: "UTR20260803112048",
    status: "Transferred",
    created_at: new Date(Date.now() - 3 * 86400000).toISOString(),
  },
  {
    id: "pay_03",
    org_id: "junagadh-shree-mahajan-gaushala",
    org_name: "Shree Junagadh Mahajan Gaushala",
    gross_amount: 348840,
    platform_fee: 0,
    net_payout: 348840,
    bank_account: "State Bank of India · A/C ****2201",
    utr_no: "Pending Audit Approval",
    status: "Pending Audit",
    created_at: new Date(Date.now() - 1 * 86400000).toISOString(),
  },
];

/* ---------------- public reads ---------------- */

export function useOrganisations(opts?: { all?: boolean }) {
  return useQuery({
    queryKey: ["organisations", opts?.all ?? false],
    queryFn: async () => {
      try {
        let q = supabase.from("organisations").select("*").order("total_raised", { ascending: false });
        if (!opts?.all) q = q.eq("status", "approved");
        const { data, error } = await q;
        if (!error && data && data.length > 0) return data as unknown as OrgRow[];
      } catch (e) {}
      return opts?.all ? fallbackOrgs : fallbackOrgs.filter((o) => o.status === "approved");
    },
  });
}

export function useOrganisation(slug: string) {
  return useQuery({
    queryKey: ["organisation", slug],
    queryFn: async () => {
      try {
        const { data, error } = await supabase.from("organisations").select("*").eq("slug", slug).maybeSingle();
        if (!error && data) return data as unknown as OrgRow;
      } catch (e) {}
      return fallbackOrgs.find((o) => o.slug === slug) ?? fallbackOrgs[0];
    },
  });
}

export function useCampaigns(opts?: { all?: boolean; orgId?: string }) {
  return useQuery({
    queryKey: ["campaigns", opts?.all ?? false, opts?.orgId ?? null],
    queryFn: async () => {
      try {
        let q = supabase.from("campaigns").select("*").order("created_at", { ascending: false });
        if (!opts?.all) q = q.eq("status", "approved");
        if (opts?.orgId) q = q.eq("org_id", opts.orgId);
        const { data, error } = await q;
        if (!error && data && data.length > 0) return data as unknown as CampaignRow[];
      } catch (e) {}
      let list = fallbackCampaigns;
      if (!opts?.all) list = list.filter((c) => c.status === "approved");
      if (opts?.orgId) list = list.filter((c) => c.org_id === opts.orgId);
      return list;
    },
  });
}

export function usePrograms() {
  return useQuery({
    queryKey: ["programs"],
    queryFn: async () => {
      try {
        const { data, error } = await supabase.from("programs").select("*").eq("status", "approved");
        if (!error && data && data.length > 0) return data;
      } catch (e) {}
      return [
        {
          id: "daily-green-fodder",
          org_id: "shree-ahmedabad-panjrapole",
          title: "Daily Gaushala Green Fodder Sponsorship",
          monthly: 1001,
          domain: "animal-seva",
          status: "approved",
        },
        {
          id: "avian-icu-support",
          org_id: "jivdaya-charitable-trust",
          title: "Avian ICU & Bird Care Unit Support",
          monthly: 1500,
          domain: "animal-seva",
          status: "approved",
        },
      ];
    },
  });
}

/* ---------------- admin reads ---------------- */

export function useApplications() {
  return useQuery({
    queryKey: ["partner_applications"],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from("partner_applications")
          .select("*")
          .order("created_at", { ascending: false });
        if (!error && data && data.length > 0) return data;
      } catch (e) {}
      return localApplications;
    },
  });
}

export function useDonations(opts?: { donorId?: string; orgId?: string }) {
  return useQuery({
    queryKey: ["donations", opts?.donorId ?? null, opts?.orgId ?? null],
    queryFn: async () => {
      try {
        let q = supabase.from("donations").select("*").order("created_at", { ascending: false }).limit(500);
        if (opts?.donorId) q = q.eq("donor_id", opts.donorId);
        if (opts?.orgId) q = q.eq("org_id", opts.orgId);
        const { data, error } = await q;
        if (!error && data && data.length > 0) return data;
      } catch (e) {}
      let list = localDonations;
      if (opts?.orgId) list = list.filter((d) => d.org_id === opts.orgId);
      return list;
    },
  });
}

export function usePayouts(orgId?: string) {
  return useQuery({
    queryKey: ["payouts", orgId ?? null],
    queryFn: async () => {
      try {
        let q = supabase.from("payouts").select("*").order("created_at", { ascending: false });
        if (orgId) q = q.eq("org_id", orgId);
        const { data, error } = await q;
        if (!error && data && data.length > 0) return data;
      } catch (e) {}
      let list = localPayouts;
      if (orgId) list = list.filter((p) => p.org_id === orgId);
      return list;
    },
  });
}

/* ---------------- mutations ---------------- */

export function useReviewApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; status: "approved" | "rejected"; note?: string }) => {
      try {
        const { data: userRes } = await supabase.auth.getUser();
        const { data, error } = await supabase
          .from("partner_applications")
          .update({
            status: input.status,
            reviewer_note: input.note ?? null,
            reviewed_at: new Date().toISOString(),
            reviewed_by: userRes.user?.id ?? null,
          })
          .eq("id", input.id)
          .select()
          .single();
        if (!error && data) return data;
      } catch (e) {}

      localApplications = localApplications.map((a) =>
        a.id === input.id ? { ...a, status: input.status, reviewer_note: input.note } : a,
      );
      const app = localApplications.find((a) => a.id === input.id);

      if (input.status === "approved" && app) {
        const newOrg: OrgRow = {
          id: app.id,
          slug: String(app.org_name).toLowerCase().replace(/[^a-z0-9]+/g, "-"),
          name: app.org_name,
          org_type: app.org_type ?? "Gaushala",
          domain: "animal-seva",
          city: app.city ?? "Gujarat",
          state: app.state ?? "Gujarat",
          summary: app.about?.slice(0, 180) ?? "",
          description: app.about ?? "",
          cover_url: null,
          contact_email: app.contact_email,
          contact_phone: app.contact_phone,
          registration_no: app.registration_no,
          status: "approved",
          verified: true,
          eligible_80g: true,
          animals_cared: app.animals_cared ?? 500,
          capacity: (app.animals_cared ?? 500) + 200,
          total_raised: 0,
          donor_count: 0,
          created_at: new Date().toISOString(),
        };
        fallbackOrgs.unshift(newOrg);
      }
      return app;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["partner_applications"] });
      qc.invalidateQueries({ queryKey: ["organisations"] });
    },
  });
}

export function useSetOrgStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; status: "approved" | "pending" | "suspended" | "rejected"; reason?: string }) => {
      try {
        await supabase
          .from("organisations")
          .update({
            status: input.status,
            verified: input.status === "approved",
            rejection_reason: input.reason ?? null,
          })
          .eq("id", input.id);
      } catch (e) {}
      const org = fallbackOrgs.find((o) => o.id === input.id);
      if (org) {
        org.status = input.status;
        org.verified = input.status === "approved";
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["organisations"] }),
  });
}

export function useSetCampaignStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; status: "approved" | "pending" | "rejected" | "suspended"; reason?: string }) => {
      try {
        await supabase.from("campaigns").update({ status: input.status }).eq("id", input.id);
      } catch (e) {}
      const camp = fallbackCampaigns.find((c) => c.id === input.id);
      if (camp) camp.status = input.status;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaigns"] }),
  });
}

export function useCreateDonation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      donor_name: string;
      donor_email?: string;
      donor_phone?: string;
      donor_pan?: string;
      anonymous?: boolean;
      amount: number;
      frequency: "once" | "monthly";
      donation_type?: "monetary" | "inkind";
      item_name?: string;
      quantity?: string;
      org_id?: string | null;
      campaign_id?: string | null;
      program_id?: string | null;
      target_label?: string | null;
      message?: string | null;
    }) => {
      const receiptNo = `DA-2026-${Math.floor(80000 + Math.random() * 19999)}`;
      const newRow = {
        id: `don_${Date.now()}`,
        receipt_no: receiptNo,
        created_at: new Date().toISOString(),
        donor_name: input.donor_name || "Generous Sevak",
        donor_email: input.donor_email ?? "",
        donor_phone: input.donor_phone ?? "",
        donor_pan: input.donor_pan ?? "",
        anonymous: !!input.anonymous,
        amount: input.amount,
        frequency: input.frequency,
        donation_type: input.donation_type ?? "monetary",
        item_name: input.item_name ?? null,
        quantity: input.quantity ?? null,
        dispatch_status: input.donation_type === "inkind" ? "Order Confirmed — Local Vendor Assigned" : "Paid",
        org_id: input.org_id ?? "shree-ahmedabad-panjrapole",
        target_label: input.target_label ?? "Daan Amrut Seva Fund",
        status: "success",
        gateway: "Razorpay / UPI",
        gateway_ref: `PAY-${Math.floor(100000 + Math.random() * 900000)}`,
      };

      try {
        const { data } = await supabase.from("donations").insert(input as any).select().single();
        if (data) {
          localDonations.unshift(data);
          return data;
        }
      } catch (e) {}

      localDonations.unshift(newRow);
      return newRow;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["donations"] }),
  });
}

export function useSubmitApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      org_name: string;
      org_type?: string;
      city: string;
      state: string;
      contact_name: string;
      contact_email: string;
      contact_phone?: string;
      registration_no: string;
      reg_80g?: string;
      reg_12a?: string;
      animals_cared?: number;
      about: string;
    }) => {
      const newApp = {
        id: `app_${Date.now()}`,
        ...input,
        domain: "animal-seva",
        status: "pending",
        created_at: new Date().toISOString(),
      };
      try {
        await supabase.from("partner_applications").insert(newApp as any);
      } catch (e) {}
      localApplications.unshift(newApp);
      return newApp;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["partner_applications"] }),
  });
}